import OrgUnitsTree from './org-units-tree';
import { UserKeys, getAllOrgUnits } from '../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import OrgUnitsGrid from './org-units-grid';
import AddOrgUnitDialog from './add-org-unit-dialog';
import EditOrgUnitDialog from './edit-org-unit-dialog';
import DeleteOrgUnitDialog from './delete-org-unit-dialog';
import OrgUnitsLimitedVizGrid from './org-units-limitedviz-grid';
import { pagesConfig } from '../../../shared/pages-config';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import { ModuleList } from '../../../shared/analytics/module-list';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';

export default function OrgUnitsPage() {
  const {
    directoryRoles,
    changeLogRoles,
    primaryDomain,
    userRoles,
    limitedVisEnabled,
    limitedVisOrgList,
  } = useUserSettings();
  const { viewType } = useUserSettings();
  const [selectedOrgUnitId, setSelectedOrgUnitId] = useState('/');
  const [openAddOrgUnitDialog, setOpenAddOrgUnitDialog] = useState(false);
  const [openEditOrgUnitDialog, setOpenEditOrgUnitDialog] = useState(false);
  const [openDeleteOrgUnitDialog, setOpenDeleteOrgUnitDialog] = useState(false);

  const {
    data: orgUnits,
    isLoading: isOrgLoading,
    isFetching: isOrgFetching,
  } = useQuery({
    queryKey: [UserKeys.OrgUnitModels],
    queryFn: () => {
      return getAllOrgUnits();
    },
    meta: {
      errorMessage: Messages.Errors.Network.Loading('org units'),
    },
  });

  const selectedOrgUnit = orgUnits?.find(
    (org) => org.orgUnitPath === selectedOrgUnitId
  );

  return (
    <SimpleBreadcrumbsPage
      breadcrumbs={[
        { label: ModuleList.modules.directory.name },
        { label: pagesConfig.orgUnits.name },
      ]}
      bookmarkKey={pagesConfig.orgUnits.value}
    >
      <Stack flexDirection="row" gap={2} height={'100%'}>
        <Card sx={{ width: 350 }}>
          <CardContent sx={{ height: '100%', px: 0, py: 1 }}>
            <OrgUnitsTree
              allOrgUnits={orgUnits || []}
              isLoading={isOrgLoading || isOrgFetching}
              selectedOrgUnitId={selectedOrgUnitId}
              setSelectedOrgUnitId={setSelectedOrgUnitId}
              setOpenAddOrgUnitDialog={setOpenAddOrgUnitDialog}
              setOpenEditOrgUnitDialog={setOpenEditOrgUnitDialog}
              setOpenDeleteOrgUnitDialog={setOpenDeleteOrgUnitDialog}
              directoryRoles={directoryRoles}
              limitedVisOrgList={limitedVisOrgList}
              limitedVisEnabled={limitedVisEnabled}
            />
          </CardContent>
        </Card>
        {limitedVisEnabled ? (
          <OrgUnitsLimitedVizGrid
            gridKey={`${
              viewType.orgUnitsGridStates?.activeGridState || 'default'
            }-${selectedOrgUnitId}`}
            selectedOrgUnitPath={selectedOrgUnitId}
            primaryDomain={primaryDomain}
            changeLogRoles={changeLogRoles}
            userRoles={userRoles}
          />
        ) : (
          <OrgUnitsGrid
            key={`${
              viewType.orgUnitsGridStates?.activeGridState || 'default'
            }-${selectedOrgUnitId}`}
            selectedOrgUnitPath={selectedOrgUnitId}
            primaryDomain={primaryDomain}
            changeLogRoles={changeLogRoles}
            userRoles={userRoles}
          />
        )}
      </Stack>
      {openAddOrgUnitDialog && (
        <AddOrgUnitDialog
          open={openAddOrgUnitDialog}
          handleClose={() => setOpenAddOrgUnitDialog(false)}
          changeLogRoles={changeLogRoles}
          orgUnits={orgUnits || []}
          selectedOrgUnitId={selectedOrgUnitId}
        />
      )}
      {openEditOrgUnitDialog && (
        <EditOrgUnitDialog
          open={openEditOrgUnitDialog}
          handleClose={() => setOpenEditOrgUnitDialog(false)}
          changeLogRoles={changeLogRoles}
          orgUnits={orgUnits || []}
          selectedOrgUnitId={selectedOrgUnitId}
        />
      )}
      {openDeleteOrgUnitDialog && orgUnits && selectedOrgUnit && (
        <DeleteOrgUnitDialog
          open={openDeleteOrgUnitDialog}
          handleClose={() => setOpenDeleteOrgUnitDialog(false)}
          selectedOrgUnit={selectedOrgUnit}
          setSelectedOrgUnitId={setSelectedOrgUnitId}
          changeLogRoles={changeLogRoles}
        />
      )}
    </SimpleBreadcrumbsPage>
  );
}
